import { useQuery } from '@apollo/client';
import storage from 'local-storage-fallback';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import {
  AuthenticatedUserQuery,
  ProviderVenuesQuery,
} from '@pv/common/graphql/operations';

import { venuesQuery } from './graphql';
import { useUserAuth } from '../providers/auth';
import { PickDeep, Unpacked } from '@pv/common/utils';

type VenueMembership = NonNullable<
  Unpacked<PickDeep<AuthenticatedUserQuery, 'venueMemberships'>>
>;

const VenuesContext = createContext<{
  venues: ProviderVenuesQuery['venues'] | null | undefined;
  venueIds: string[];
  setCurrentVenueMemberships: (
    vms: VenueMembership[],
    redirect?: boolean
  ) => void;
  loading: boolean;
  error: Error | null;
  refetchVenues: () => void;
}>({
  venues: null,
  venueIds: [],
  loading: false,
  error: null,
  setCurrentVenueMemberships: () => {},
  refetchVenues: () => {},
});

export const useVenues = () => useContext(VenuesContext);

const cvmKey = 'vmids';

export const storeCurrentVenueMemberships = (
  venueMemberships: VenueMembership[]
) => {
  storage.setItem(cvmKey, JSON.stringify(map(venueMemberships, (vm) => vm.id)));
};

const VenuesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { user } = useUserAuth();
  const [venueMemberships, setVenueMemberships] = useState<VenueMembership[]>();

  const setCurrentVenueMembershipsAndStore = useCallback(
    (vms: VenueMembership[]) => {
      storeCurrentVenueMemberships(vms);
      setVenueMemberships(vms);
    },
    [setVenueMemberships]
  );

  const setCurrentVenueMemberships = useCallback(
    (vms: VenueMembership[], redirect = false) => {
      if (isEmpty(vms)) {
        return;
      }
      setCurrentVenueMembershipsAndStore(vms);
      if (redirect) {
        navigate('/events');
      }
    },
    [navigate, setCurrentVenueMembershipsAndStore]
  );

  useEffect(() => {
    if (user && isEmpty(user.activeVenueMemberships)) {
      return;
    } else if (user && !venueMemberships) {
      let currentVenueMembershipIds = storage.getItem(cvmKey);
      if (currentVenueMembershipIds) {
        try {
          currentVenueMembershipIds = JSON.parse(currentVenueMembershipIds);
        } catch (e) {
          // pass
        }
      }
      let vms = filter(user.activeVenueMemberships, (vm) =>
        includes(currentVenueMembershipIds, vm.id)
      );
      if (isEmpty(vms)) {
        vms = [user.activeVenueMemberships[0]];
      }
      setCurrentVenueMembershipsAndStore(vms);
    }
  }, [user, venueMemberships, setCurrentVenueMembershipsAndStore]);

  const venueIds = useMemo(
    () => map(venueMemberships, (vm) => vm.venue.id),
    [venueMemberships]
  );

  const variables = { ids: venueIds };
  const skip = isEmpty(venueIds);
  const { data, refetch, loading, error } = useQuery<ProviderVenuesQuery>(
    venuesQuery,
    {
      variables,
      skip,
      errorPolicy: 'all', // This allows us to get error even if data is undefined
    }
  );
  const venues = data?.venues;

  useEffect(() => {
    if (!venueMemberships?.length) return;

    if (venueMemberships.length === 1) {
      const venue = venueMemberships[0].venue;
      const venueData = venues?.find((v) => v.id === venue.id);
      posthog.group('venue', venue.publicId, {
        name: venue.name,
        subscription_tier: venueData?.subscription?.tier,
      });
    } else if (user?.organization) {
      posthog.group('venue', user.organization.publicId, {
        name: `${user.organization.name} (All Venues)`,
      });
    }
  }, [venueMemberships, venues, posthog, user]);

  const memoizedValue = useMemo(
    () => ({
      venues,
      venueIds,
      loading,
      error: error || null,
      setCurrentVenueMemberships,
      refetchVenues: refetch,
    }),
    [venues, venueIds, loading, error, setCurrentVenueMemberships, refetch]
  );

  return (
    <VenuesContext.Provider value={memoizedValue}>
      {children}
    </VenuesContext.Provider>
  );
};

export default VenuesProvider;
